// 소개서(PDF·브랜드 링크)에 담을 블록 목록 — **팝업과 서버가 같은 표를 본다**.
//
// 여기가 단일 원천이다. 팝업에서 고른 id 를 그대로 서버로 보내고 렌더러가 그 id 로 판정한다.
// 목록을 두 곳에 두면 체크박스는 있는데 실제로는 안 빠지는(또는 그 반대) 상태가 생긴다.
//
// `internalOnly` 는 **브랜드에 보내면 곤란한 것**이다. 매출·확정 딜은 우리 협상 정보라
// 수신 브랜드가 볼 이유가 없다. 기본값에서 빼두고, 켜는 건 사용자 판단으로 남긴다.
export interface BrandKitBlock {
  id: BrandKitBlockId
  label: string
  /** 무엇이 담기는지 한 줄 — 체크 전에 내용을 알 수 있어야 고를 수 있다. */
  hint: string
  internalOnly?: boolean
  /**
   * 아직 소개서에 담기지 않는 블록 — 체크해도 문서엔 안 나온다.
   *
   * 목록에서 빼지 않고 **비활성으로 남기는** 이유는, 없애면 "이 항목은 왜 없나"를 매번 다시
   * 묻게 되기 때문이다. 자리를 두고 준비 중임을 밝히는 편이 정직하다.
   * 매출은 PublicBrandProfile 타입에 필드 자체가 없다(brandKit.ts 상단 주석 — 의도적 배제).
   */
  unavailable?: string
}

export type BrandKitBlockId =
  | 'hero'
  | 'channels'
  | 'brandedContent'
  | 'channelPerformance'
  | 'revenue'
  | 'business'
  | 'recent'

// 순서 규칙: **대외 공개 블록 먼저, `internalOnly` 는 목록 맨 아래에 연속으로.**
// 섞어 놓으면 "이건 나가도 되나"를 항목마다 다시 판단하게 된다 — 경계가 눈에 보여야 실수가 준다.
export const BRAND_KIT_BLOCKS: BrandKitBlock[] = [
  { id: 'hero', label: '프로필 히어로', hint: '이름 · 핸들 · 브랜디드 인게이지먼트' },
  { id: 'channels', label: '채널 구독자', hint: '총 구독자 · 채널별 구독자' },
  {
    id: 'brandedContent',
    label: '브랜디드 콘텐츠',
    // 상한을 밝힌다 — 고른 채널 전체에서 조회수 상위 12개라, 성과 큰 채널이 자리를 다 먹을 수 있다.
    // 그 사실을 모르면 "TikTok 을 골랐는데 왜 안 나오지"를 문서를 받고 나서야 알게 된다.
    hint: '선택한 채널 전체에서 조회수 상위 12개 · 조회수 · 시청률',
  },
  { id: 'channelPerformance', label: '채널 퍼포먼스', hint: '롱폼 · 숏폼 평균 조회수 / 시청률' },
  {
    id: 'recent',
    label: '최근 협업',
    hint: '선택한 채널 전체에서 발행일 최신 12개 · 브랜드 · 조회수',
  },
  {
    id: 'revenue',
    label: '매출 추이',
    hint: '월별 매출 · 누적 (억 원)',
    internalOnly: true,
    unavailable: '소개서 미지원',
  },
  {
    id: 'business',
    label: '확정 비즈니스',
    hint: '최신 8건 (브랜드 · 범위 · 시점)',
    internalOnly: true,
  },
]

/** 기본 선택 — 내부 전용은 빼고 시작한다. 실수로 나가는 쪽보다 실수로 빠지는 쪽이 낫다. */
export const DEFAULT_BRAND_KIT_BLOCKS: BrandKitBlockId[] = BRAND_KIT_BLOCKS.filter(
  (b) => !b.internalOnly && !b.unavailable,
).map((b) => b.id)
